import { appConfig } from '@/config/app-config';
import BPCGenericApiResponse from '@core/interfaces/bpc-generic-api-response';
// profile/index.ts
import { Module, MutationTree, GetterTree, ActionTree } from 'vuex';
import { TenantState, Center } from '@core/interfaces/tenant-state';
import { RootState } from '@core/interfaces/root-state';
import router from '@app/router/index.APP_TARGET';

import store from '@app/store';
import BPCApiRouteHelper from '@core/helpers/bpc-api-route.helper';
import { bpcApi } from '@core/services/bpc-api.service';
import AuthorizationService from '@auth/services/authorization.service';

const bpcRouterHelper = new BPCApiRouteHelper();

// Initial state
const state: TenantState = {
  tenants: JSON.parse(localStorage.getItem('tenants')),
  selectedTenantId: null,
  loading: false,
  tenantAlias: null,
};

// Synchronous functions to get data from state (can be composed properties)
const getters: GetterTree<TenantState, RootState> = {
  tenants(state): Center[] {
    return state.tenants;
  },
  selectedTenant(state): Center {
    if (state.selectedTenantId && state.tenants) {
      const tenant = state.tenants.find(tenant => {
        return tenant.id === state.selectedTenantId;
      });
      return tenant;
    }
    return null;
  },
  selectedTenantId(state): number {
    return Number(state.selectedTenantId);
  },
  isLoading(state): boolean {
    return !!state.loading;
  },

  tenantAlias(state): string {
    return state.tenantAlias;
  },
  tenantDefaultLanguage(state, getters): string {
    return getters.selectedTenant?.center_setting?.default_language;
  },
  tenantAvailableLanguages(state, getters): string {
    return getters.selectedTenant?.center_setting?.available_languages;
  },
};

// Asynchornous functions to get data usually from an api call
const actions: ActionTree<TenantState, RootState> = {
  // Login method
  changeTenant({ commit }, payload: Center) {
    console.log('payload', payload);
    commit('selectTenant', payload);

    const $authorizationService = new AuthorizationService();
    const homePageRoute = $authorizationService.getTenantHomePage(payload);
    router.push(homePageRoute).then(() => {
      router.go(null);
    });
  },
  initializeTenantByAlias({ commit }, alias: string) {
    return new Promise((resolve, reject) => {
      bpcApi.get(`centers/${alias}`).subscribe(
        (response: BPCGenericApiResponse) => {
          commit('selectTenantId', response.data.id);
          commit('storeTenants', [response.data]);
          commit('setTenantAlias', response.data.alias);
          commit('centerSettings/updateCenterSettings', response.data.center_setting, {
            root: true,
          });
          resolve(response.data);
        },
        error => {
          reject(error);
        },
      );
    });
  },
  getTenants(
    { commit },
    payload: {
      selected_tenant_id?: number;
    } = { selected_tenant_id: null },
  ): void {
    commit('startLoading');
    bpcApi.get(bpcRouterHelper.getMyTenantsPath(), {}, 'admin').subscribe(
      (res: BPCGenericApiResponse) => {
        commit('storeTenants', res.data);
        if (payload.selected_tenant_id) {
          const tenant = res.data.find(center => Number(center.id) === payload.selected_tenant_id);
          commit('selectTenant', tenant);
          store.commit('centerSettings/updateCenterSettings', tenant.center_setting);
        } else if (res.data[0]) {
          commit('selectTenant', res.data[0]);
          store.commit('centerSettings/updateCenterSettings', res.data[0].center_setting);
        }
      },
      error => {
        console.log(error);
        commit('stopLoading');
      },
    );
  },
};

// The vuex way of changing a state property
export const mutations: MutationTree<TenantState> = {
  stopLoading(state) {
    state.loading = false;
  },
  startLoading(state) {
    state.loading = true;
  },

  storeTenants(state, data: Center[]) {
    state.tenants = data;
    localStorage.setItem('tenants', JSON.stringify(data));
  },
  setTenantAlias(state, tenantAlias) {
    if (tenantAlias && tenantAlias !== appConfig.globalTenantAlias) {
      state.tenantAlias = tenantAlias;
      localStorage.setItem('tenantAlias', tenantAlias);
    } else {
      state.tenantAlias = null;
      localStorage.removeItem('tenantAlias');
    }
  },
  selectTenant(state, data: Center) {
    if (data.alias && data.alias !== appConfig.globalTenantAlias) {
      localStorage.setItem('tenant_id', `${data.id}`);
      localStorage.setItem('tenantAlias', `${data.alias}`);
      /* store.commit('auth/setUserLang', `${data.center_setting.default_language}`); */
      state.tenantAlias = data.alias;
      state.selectedTenantId = data.id;
    }
  },
  selectTenantId(state, data: number) {
    localStorage.setItem('tenant_id', `${data}`);
    /* store.commit('auth/setUserLang', `${data.center_setting.default_language}`); */
    state.selectedTenantId = data;
  },

  clearTenants(state) {
    state.tenants = null;
    localStorage.removeItem('tenants');
  },

  clearSelectedTenant(state) {
    state.selectedTenantId = null;
    localStorage.removeItem('tenant_id');
  },
  clearTenantAlias(state) {
    state.tenantAlias = null;
    localStorage.removeItem('tenantAlias');
  },
};

export const TenantStoreModule: Module<TenantState, RootState> = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
